import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';
import { Button } from '@/components/ui/button';
import { DataSurface } from '@/components/platform/DataSurface';
import { MetricStrip } from '@/components/platform/MetricStrip';
import PeopleFilters, { PeopleFilterState } from '@/components/people/PeopleFilters';
import PeopleTable from '@/components/people/PeopleTable';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { usePeopleList } from '@/hooks/people/usePeopleList';
import { usePeopleMetrics } from '@/hooks/people/usePeopleMetrics';
import peopleService from '@/lib/api/people';
import { useToast } from '@/components/ui/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const PAGE_SIZE = 50;

const DATE_PRESET_LABELS: Record<string, string> = {
  last_7_days: 'Last 7 days',
  last_30_days: 'Last 30 days',
  last_90_days: 'Last 90 days',
  all_time: 'All time',
};

const DATE_PRESET_COMPARISON: Record<string, string> = {
  last_7_days: 'vs prev 7d',
  last_30_days: 'vs prev 30d',
  last_90_days: 'vs prev 90d',
};

const initialFilters: PeopleFilterState = {
  search: '',
  buyingIntent: [],
  qualificationStatus: [],
  datePreset: 'last_30_days',
};

const DATE_PRESET_OPTIONS = [
  { label: 'Last 7 days', value: 'last_7_days' },
  { label: 'Last 30 days', value: 'last_30_days' },
  { label: 'Last 90 days', value: 'last_90_days' },
  { label: 'All time', value: 'all_time' },
] as const;

const formatMetricTrend = (
  change: number | null | undefined,
): { value: string; tone: 'positive' | 'negative' | 'neutral' } | undefined => {
  if (change == null) return undefined;
  if (change === 0) return { value: '0%', tone: 'neutral' };
  const abs = Math.abs(change);
  const compactValue = abs >= 1000 ? `${(abs / 100).toFixed(1)}x` : `${abs}%`;
  return {
    value: `${change > 0 ? '+' : '-'}${compactValue}`,
    tone: change > 0 ? 'positive' : 'negative',
  };
};

const PeoplePage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { toast } = useToast();

  const [filters, setFilters] = useState<PeopleFilterState>(() => ({ ...initialFilters }));
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters.buyingIntent, filters.qualificationStatus, filters.datePreset, debouncedSearch]);

  const dateFilters = useMemo(() => {
    if (filters.datePreset === 'all_time') {
      return { date_from: undefined, date_to: undefined };
    }
    const now = new Date();
    const days = filters.datePreset === 'last_7_days' ? 7
      : filters.datePreset === 'last_90_days' ? 90
      : 30;
    const from = new Date(now);
    from.setDate(from.getDate() - days);
    return {
      date_from: from.toISOString(),
      date_to: now.toISOString(),
    };
  }, [filters.datePreset]);

  const { people, total, isLoading, error } = usePeopleList(currentOrganization?.id, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    search: debouncedSearch || undefined,
    buying_intent: filters.buyingIntent.length > 0 ? filters.buyingIntent.join(',') : undefined,
    qualification_status: filters.qualificationStatus.length > 0 ? filters.qualificationStatus.join(',') : undefined,
    date_from: dateFilters.date_from,
    date_to: dateFilters.date_to,
    enabled: !!currentOrganization?.id,
  });

  const { data: metrics, isLoading: metricsLoading } = usePeopleMetrics(
    currentOrganization?.id,
    { date_from: dateFilters.date_from, date_to: dateFilters.date_to },
  );

  // Keep page in bounds
  useEffect(() => {
    if (!total) return;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > totalPages) setPage(1);
  }, [page, total]);

  const clearFilters = useCallback(() => {
    setFilters({ ...initialFilters });
  }, []);

  const handleExportCsv = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setIsExporting(true);
    try {
      await peopleService.exportPeopleCsv(currentOrganization.id, {
        search: debouncedSearch || undefined,
        buying_intent: filters.buyingIntent.length > 0 ? filters.buyingIntent.join(',') : undefined,
        qualification_status: filters.qualificationStatus.length > 0 ? filters.qualificationStatus.join(',') : undefined,
        date_from: dateFilters.date_from,
        date_to: dateFilters.date_to,
      });
    } catch (e) {
      console.error('CSV export failed:', e);
      toast({
        title: 'CSV export failed',
        description: 'Unable to download the export right now. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  }, [currentOrganization?.id, debouncedSearch, filters, dateFilters, toast]);

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) {
    return <div className="p-4 text-center">Please log in to view people.</div>;
  }

  if (!currentOrganization?.id) {
    return <div className="p-4 text-center">Select an organization to view people.</div>;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Helmet>
        <title>People | Hobbes</title>
      </Helmet>
      <div className="flex min-h-0 flex-col">
        <PageHeader title="People" titleTooltip="Visitors identified from demo sessions." />

        <div className="px-4 pb-3 pt-4 sm:px-6 md:px-8">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-[13px] font-medium text-foreground">Buyer index</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Known visitors, intent, and qualification for {DATE_PRESET_LABELS[filters.datePreset] ?? filters.datePreset}.
              </p>
            </div>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-8 shrink-0 justify-between rounded-md border border-border bg-background px-3 text-sm text-muted-foreground shadow-none hover:bg-accent hover:text-accent-foreground"
                >
                  <span className="truncate text-left">{DATE_PRESET_LABELS[filters.datePreset] ?? filters.datePreset}</span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[180px] p-2" align="end">
                <div className="flex flex-col gap-1">
                  {DATE_PRESET_OPTIONS.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      className={`flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm text-left hover:bg-accent ${
                        filters.datePreset === preset.value ? 'font-medium bg-accent' : ''
                      }`}
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, datePreset: preset.value }));
                        setDatePickerOpen(false);
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <MetricStrip
            items={[
              {
                label: 'People',
                value: metricsLoading ? '...' : (metrics?.total_users ?? 0).toLocaleString(),
                meta: DATE_PRESET_COMPARISON[filters.datePreset] ?? 'Current range',
                trend: filters.datePreset !== 'all_time' ? formatMetricTrend(metrics?.total_users_change) : undefined,
              },
              {
                label: 'Avg sessions',
                value: metricsLoading ? '...' : (metrics?.avg_demos_per_user ?? 0),
                meta: 'Per person',
                trend: filters.datePreset !== 'all_time' ? formatMetricTrend(metrics?.avg_demos_per_user_change) : undefined,
              },
              {
                label: 'High intent',
                value: metricsLoading ? '...' : (metrics?.high_intent_users ?? 0).toLocaleString(),
                meta: 'Buying signals',
                trend: filters.datePreset !== 'all_time' ? formatMetricTrend(metrics?.high_intent_users_change) : undefined,
              },
              {
                label: 'Qualified',
                value: metricsLoading ? '...' : (metrics?.qualified_leads ?? 0).toLocaleString(),
                meta: 'Meets criteria',
                trend: filters.datePreset !== 'all_time' ? formatMetricTrend(metrics?.qualified_leads_change) : undefined,
              },
            ]}
          />
        </div>

        <div className="px-4 pb-3 sm:px-6 md:px-8">
          <PeopleFilters
            filters={filters}
            onFiltersChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            onReset={clearFilters}
            onExportCsv={handleExportCsv}
            isExporting={isExporting}
          />
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-8">
          {error && (
            <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load people'}
            </div>
          )}
          <DataSurface>
            <PeopleTable
              people={people}
              isLoading={isLoading}
              offset={(page - 1) * PAGE_SIZE}
            />
          </DataSurface>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PeoplePage;
